import React, { useState, useEffect } from 'react';
import { Card, Image as Img, Typography, Flex, Empty, Skeleton } from 'antd';
import { useNavigate } from 'react-router-dom';
import { EditOutlined, DeleteOutlined, SettingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { AdvertEntity } from '../../../domain';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal.component';
import './AdvertCard.styles.css';

const { Title } = Typography;

interface AdvertCardProps {
  advertModel: AdvertEntity;
  onDelete?: (advertUUID: string) => void;
}

const AdvertCard: React.FC<AdvertCardProps> = ({ advertModel, onDelete }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
  const [isImageValid, setIsImageValid] = useState<boolean>(true);
  
  const allImages = (advertModel.attachments || []).filter(a => (a.type || '').startsWith('image') || a.type === 'image')
  const hasImages = allImages.length > 0;
  const mainImageUrl = hasImages ? allImages[0].url : null;

  useEffect(() => {
    if (mainImageUrl) {
      const img = new Image();
      img.onload = () => setIsImageValid(true);
      img.onerror = () => setIsImageValid(false);
      img.src = mainImageUrl;
      
      return () => {
        img.onload = null;
        img.onerror = null;
      };
    }
  }, [mainImageUrl]);

  if (!advertModel || !advertModel.uuid) {
    return (
      <Card style={{ width: 300 }}>
        <Skeleton active />
      </Card>
    );
  }

  const handleEdit = () => { navigate(`/adverts/${advertModel.uuid}/edit`, { state: { advert: advertModel } }); };

  const handleDelete = () => {
    setIsDeleteModalVisible(true);
  };

  const handleDeleteConfirm = () => {
    if (onDelete) {
      onDelete(advertModel.uuid);
    }
    setIsDeleteModalVisible(false);
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalVisible(false);
  };

  const handleViewClick = () => { navigate(`/adverts/${advertModel.uuid}`, { state: { advert: advertModel } }); };

  const cardActions = [
    <SettingOutlined key="options" onClick={(e) => { e.stopPropagation(); handleViewClick(); }} />,
    <EditOutlined key="edit" onClick={(e) => { e.stopPropagation(); handleEdit(); }} />,
    <DeleteOutlined key="delete" onClick={(e) => { e.stopPropagation(); handleDelete(); }} />,
  ];

  return (
    <Card
      className="advert-card"
      hoverable
      title={
        <Flex justify="space-between" align="center">
          <Title level={5} style={{ margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {advertModel.title.Value}
          </Title>
        </Flex>
      }
      actions={cardActions}
      style={{ width: 300, minHeight: 300 }}
    >
      
  <Img.PreviewGroup items={(advertModel.attachments || []).map(a => a.url)}>
        <Flex align="center" justify="center" style={{ width: '100%', height: '100%' }}>
          {hasImages && isImageValid ? (
            <Img src={mainImageUrl || ''} style={{ width: '200px', height: '200px', objectFit: 'cover' }} />
          ) : (
            <Empty style={{ width: '200px', height: '200px', objectFit: 'cover' }} />
          )}
        </Flex>
      </Img.PreviewGroup>
      
      <ConfirmationModal
        title={t('advert-delete-title', { ns: 'modals' })}
        content={t('advert-delete-message', { ns: 'modals' })}
        isVisible={isDeleteModalVisible}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </Card>
  );
};

export default AdvertCard;